/* ============================================================
   MM-REGISTRY - Mae & Mini custom gift registry
   Replaces Swym Gift Lists. Server-backed (Upstash via dashboard),
   tied to Shopify customer accounts. Exposes window.MMRegistry.
   ============================================================ */
(function () {
  'use strict';

  const cfg = (window.MMRegistryConfig || {});
  const SYNC_URL = cfg.syncUrl || '';
  const CUSTOMER_ID = cfg.customerId || null;
  const CUSTOMER_EMAIL = cfg.customerEmail || null;
  const TOKEN = cfg.token || '';
  const TS = cfg.ts || '';
  const ENABLED = !!(SYNC_URL && CUSTOMER_ID && TOKEN && TS);

  const state = {
    registries: [],
    ready: false,
    loading: false,
  };
  const listeners = [];

  function emit(event, payload) {
    listeners.forEach(function (l) {
      try { l(event, payload); } catch (e) { /* swallow */ }
    });
  }

  function on(callback) {
    listeners.push(callback);
    if (state.ready) callback('ready', { registries: state.registries.slice() });
  }

  function track(name, params) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params || {});
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: name }, params || {}));
      }
    } catch (e) { /* swallow */ }
  }

  // Shopify NEW customer accounts ignore /account/login?return_url entirely
  // (302 to the shopify.com account hub, parameter dropped). Only this OAuth
  // route round-trips back to the storefront page we came from.
  function loginUrl(returnPath) {
    var path = returnPath || (window.location.pathname + window.location.search);
    return '/customer_authentication/login?return_to=' + encodeURIComponent(path);
  }

  function authHeaders() {
    return {
      'Content-Type': 'application/json',
      'X-MM-Customer-Id': String(CUSTOMER_ID || ''),
      'X-MM-Token': TOKEN,
      'X-MM-Ts': TS,
    };
  }

  function call(path, method, body) {
    if (!ENABLED) return Promise.reject(new Error('not_enabled'));
    const init = {
      method: method || 'GET',
      headers: authHeaders(),
      mode: 'cors',
      credentials: 'omit',
    };
    if (body !== undefined) init.body = JSON.stringify(body);
    return fetch(SYNC_URL.replace(/\/+$/, '') + path, init).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) {
          const err = new Error(data.error || ('http ' + r.status));
          err.status = r.status;
          err.data = data;
          throw err;
        }
        return data;
      });
    });
  }

  function showToast(message) {
    let toast = document.querySelector('[data-mm-registry-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.setAttribute('data-mm-registry-toast', '');
      toast.style.cssText = 'position:fixed;bottom:1.5rem;left:50%;transform:translateX(-50%);background:#2C3E50;color:#fff;padding:0.875rem 1.25rem;border-radius:999px;font-family:DM Sans,sans-serif;font-size:0.875rem;font-weight:500;box-shadow:0 8px 24px rgba(0,0,0,0.18);z-index:9999;opacity:0;transition:opacity 0.2s ease;pointer-events:none;';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.style.opacity = '1';
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { toast.style.opacity = '0'; }, 2500);
  }

  // ----- Public API -----

  function list(opts) {
    opts = opts || {};
    if (state.loading && !opts.force) {
      return Promise.resolve(state.registries.slice());
    }
    if (!ENABLED) return Promise.resolve([]);
    state.loading = true;
    return call('/customer/' + encodeURIComponent(CUSTOMER_ID) + '/registries').then(function (data) {
      state.registries = Array.isArray(data.registries) ? data.registries : [];
      state.loading = false;
      emit('listed', { registries: state.registries.slice() });
      return state.registries.slice();
    }).catch(function (err) {
      state.loading = false;
      console.warn('[mm-registry] list failed', err);
      return [];
    });
  }

  function create(meta) {
    return call('/registry', 'POST', Object.assign({
      ownerEmail: CUSTOMER_EMAIL,
      isPublic: true,
    }, meta)).then(function (data) {
      const reg = data.registry;
      state.registries.push(reg);
      track('mm_registry_create', { event_type: reg.eventType, registry_id: reg.id });
      emit('created', { registry: reg });
      return reg;
    });
  }

  function get(id) {
    return call('/registry/' + encodeURIComponent(id)).then(function (data) {
      const reg = data.registry;
      const idx = state.registries.findIndex(function (r) { return r.id === reg.id; });
      if (idx >= 0) state.registries[idx] = reg; else state.registries.push(reg);
      emit('fetched', { registry: reg });
      return reg;
    });
  }

  function update(id, meta) {
    return call('/registry/' + encodeURIComponent(id), 'PATCH', meta).then(function (data) {
      const reg = data.registry;
      const idx = state.registries.findIndex(function (r) { return r.id === reg.id; });
      if (idx >= 0) state.registries[idx] = reg;
      emit('updated', { registry: reg });
      return reg;
    });
  }

  function del(id) {
    return call('/registry/' + encodeURIComponent(id), 'DELETE').then(function () {
      state.registries = state.registries.filter(function (r) { return r.id !== id; });
      track('mm_registry_delete', { registry_id: id });
      emit('deleted', { id: id });
      return true;
    });
  }

  function addItem(registryId, productData) {
    if (!productData || !productData.productId) return Promise.reject(new Error('no_product'));
    const body = {
      productId: String(productData.productId),
      variantId: productData.variantId ? String(productData.variantId) : '',
      variantTitle: productData.variantTitle || '',
      handle: productData.handle || '',
      title: productData.title || '',
      imageUrl: productData.imageUrl || '',
      url: productData.url || (productData.handle ? '/products/' + productData.handle : ''),
      price: typeof productData.price === 'number' ? productData.price : parseInt(productData.price || '0', 10),
      quantityWanted: productData.quantityWanted || 1,
    };
    return call('/registry/' + encodeURIComponent(registryId) + '/items', 'POST', body).then(function (data) {
      const reg = data.registry;
      const idx = state.registries.findIndex(function (r) { return r.id === reg.id; });
      if (idx >= 0) state.registries[idx] = reg;
      track('mm_registry_add_item', {
        registry_id: reg.id,
        product_id: body.productId,
        product_handle: body.handle,
        source: productData.source || 'unknown',
      });
      emit('item_added', { registry: reg, productId: body.productId });
      return reg;
    });
  }

  function removeItem(registryId, itemId) {
    return call('/registry/' + encodeURIComponent(registryId) + '/items/' + encodeURIComponent(itemId), 'DELETE').then(function (data) {
      const reg = data.registry;
      const idx = state.registries.findIndex(function (r) { return r.id === reg.id; });
      if (idx >= 0) state.registries[idx] = reg;
      track('mm_registry_remove_item', { registry_id: reg.id, item_id: itemId });
      emit('item_removed', { registry: reg, itemId: itemId });
      return reg;
    });
  }

  function updateItem(registryId, itemId, patch) {
    return call('/registry/' + encodeURIComponent(registryId) + '/items/' + encodeURIComponent(itemId), 'PATCH', patch).then(function (data) {
      const reg = data.registry;
      const idx = state.registries.findIndex(function (r) { return r.id === reg.id; });
      if (idx >= 0) state.registries[idx] = reg;
      emit('item_updated', { registry: reg, itemId: itemId });
      return reg;
    });
  }

  function isItemInAnyRegistry(productId) {
    const pid = String(productId);
    for (let i = 0; i < state.registries.length; i++) {
      const items = state.registries[i].items || [];
      for (let j = 0; j < items.length; j++) {
        if (String(items[j].productId) === pid) return state.registries[i].id;
      }
    }
    return null;
  }

  function shareUrl(registry) {
    if (!registry || !registry.shareToken) return '';
    return window.location.origin + '/pages/lista-compartida?t=' + encodeURIComponent(registry.shareToken);
  }

  // ----- Picker modal (PDP "Add to registry") -----
  function ensureModalStyles() {
    if (document.getElementById('mm-rg-modal-styles')) return;
    const style = document.createElement('style');
    style.id = 'mm-rg-modal-styles';
    style.textContent = [
      '.mm-rg-modal{position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;padding:1rem;background:rgb(0 0 0 / 0.45);opacity:0;pointer-events:none;transition:opacity 0.18s ease;font-family:DM Sans,-apple-system,sans-serif;color:#2C3E50;}',
      '.mm-rg-modal.is-open{opacity:1;pointer-events:auto;}',
      '.mm-rg-modal__card{width:100%;max-width:440px;background:#fff;border-radius:20px;display:flex;flex-direction:column;max-height:90vh;overflow:hidden;transform:translateY(8px);transition:transform 0.2s ease;box-shadow:0 24px 60px -20px rgb(0 0 0 / 0.4);}',
      '.mm-rg-modal.is-open .mm-rg-modal__card{transform:translateY(0);}',
      '.mm-rg-modal__head{display:flex;align-items:center;justify-content:space-between;padding:1.25rem 1.5rem;border-bottom:1px solid rgb(44 62 80 / 0.1);}',
      '.mm-rg-modal__title{font-family:Playfair Display,Georgia,serif;font-size:1.25rem;font-weight:500;margin:0;line-height:1.2;color:#2C3E50;}',
      '.mm-rg-modal__close{width:32px;height:32px;border-radius:50%;background:#F4F6F8;color:#2C3E50;border:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;}',
      '.mm-rg-modal__close svg{width:14px;height:14px;}',
      '.mm-rg-modal__body{overflow-y:auto;padding:1rem 1.5rem 1.5rem;}',
      '.mm-rg-modal__item{display:flex;align-items:center;gap:0.85rem;padding:0.85rem 1rem;border:1px solid rgb(44 62 80 / 0.1);border-radius:12px;background:#fff;cursor:pointer;font-family:inherit;font-size:0.9375rem;color:#2C3E50;text-align:left;transition:background 0.15s ease,border-color 0.15s ease;width:100%;margin-bottom:0.5rem;}',
      '.mm-rg-modal__item:hover:not([disabled]){background:#F4F6F8;border-color:rgb(44 62 80 / 0.25);}',
      '.mm-rg-modal__item[disabled]{opacity:0.5;cursor:not-allowed;}',
      '.mm-rg-modal__item-icon{width:36px;height:36px;border-radius:50%;background:#F4F6F8;color:#A8B8C8;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;}',
      '.mm-rg-modal__item-icon svg{width:16px;height:16px;}',
      '.mm-rg-modal__item-text{display:flex;flex-direction:column;gap:0.15rem;flex:1;min-width:0;}',
      '.mm-rg-modal__item-title{font-weight:500;font-size:0.9375rem;color:#2C3E50;}',
      '.mm-rg-modal__item-meta{font-size:0.75rem;color:rgb(44 62 80 / 0.55);}',
      '.mm-rg-modal__divider{border:none;border-top:1px solid rgb(44 62 80 / 0.1);margin:0.5rem 0;}',
      '.mm-rg-modal__loading{text-align:center;padding:2rem 1rem;color:rgb(44 62 80 / 0.55);font-size:0.875rem;}',
    ].join('\n');
    document.head.appendChild(style);
  }

  function ensureModal() {
    ensureModalStyles();
    let modal = document.querySelector('[data-mm-rg-modal]');
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'mm-rg-modal';
    modal.setAttribute('data-mm-rg-modal', '');
    modal.innerHTML =
      '<div class="mm-rg-modal__card">' +
        '<div class="mm-rg-modal__head">' +
          '<h2 class="mm-rg-modal__title" data-mm-rg-modal-title>Agregar a tu lista</h2>' +
          '<button type="button" class="mm-rg-modal__close" data-mm-rg-modal-close aria-label="Cerrar">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="mm-rg-modal__body" data-mm-rg-modal-body>' +
          '<p class="mm-rg-modal__loading">Cargando tus listas...</p>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modal);
    modal.addEventListener('click', function (e) {
      if (e.target === modal || e.target.closest('[data-mm-rg-modal-close]')) {
        closePicker();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closePicker();
    });
    return modal;
  }

  function closePicker() {
    const modal = document.querySelector('[data-mm-rg-modal]');
    if (modal) modal.classList.remove('is-open');
  }

  function showLoginExplainer(opts) {
    opts = opts || {};
    const source = opts.source || 'picker';
    track('mm_registry_login_wall_shown', { source: source });
    const modal = ensureModal();
    const title = modal.querySelector('[data-mm-rg-modal-title]');
    if (title) title.textContent = opts.title || 'Guarda tu mesa de regalos';
    const body = modal.querySelector('[data-mm-rg-modal-body]');
    if (body) {
      const lead = opts.lead || 'Para guardar tu lista necesitas una cuenta gratis.';
      body.innerHTML =
        '<div class="mm-rg-modal__login">' +
          '<p style="margin:0 0 0.75rem;font-size:0.9375rem;line-height:1.55;">' + lead + ' <strong>Sin contraseñas:</strong> escribes tu correo, te llega un código de 6 dígitos y regresas aquí automáticamente.</p>' +
          '<p style="margin:0 0 1rem;font-size:0.8125rem;color:rgb(44 62 80 / 0.6);">Tu lista viaja contigo: todo lo que ya agregaste se guarda en tu cuenta.</p>' +
          '<button type="button" data-mm-rg-login-continue style="display:block;width:100%;padding:0.875rem 1.5rem;background:#2c3e50;color:#fff;border:none;border-radius:999px;font-family:inherit;font-size:0.9375rem;font-weight:600;cursor:pointer;">Continuar con mi correo</button>' +
        '</div>';
      const btn = body.querySelector('[data-mm-rg-login-continue]');
      if (btn) {
        btn.addEventListener('click', function () {
          track('mm_registry_login_continue', { source: source });
          window.location.href = loginUrl(opts.returnPath);
        });
      }
    }
    modal.classList.add('is-open');
  }

  // ----- Guest draft (localStorage, patroon = bewezen mm-wishlist) -----
  // Gasten bouwen hun lijst zonder account; het account komt pas op het
  // deelmoment. Bij login migreert de draft automatisch naar de server.
  const DRAFT_KEY = 'mm_registry_draft_v1';

  function draftLoad() {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return null;
      const d = JSON.parse(raw);
      if (!d || typeof d !== 'object' || Array.isArray(d)) return null;
      d.items = Array.isArray(d.items) ? d.items : [];
      return d;
    } catch (e) { return null; }
  }

  function draftSave(d) {
    try {
      d.updatedAt = Date.now();
      localStorage.setItem(DRAFT_KEY, JSON.stringify(d));
      return true;
    } catch (e) { return false; }
  }

  function draftClear() {
    try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* swallow */ }
    emit('draft_cleared', {});
  }

  function hasDraft() {
    const d = draftLoad();
    return !!(d && (d.items.length > 0 || d.title));
  }

  // Pseudo-registry vorm zodat de owner-pagina dezelfde render-pipeline kan gebruiken
  function getDraft() {
    const d = draftLoad();
    if (!d) return null;
    return {
      id: 'draft',
      isDraft: true,
      title: d.title || 'Mi mesa de regalos',
      eventType: d.eventType || 'baby_shower',
      eventDate: d.eventDate,
      dueDate: d.dueDate,
      recipientName: d.recipientName,
      message: d.message,
      items: d.items.slice(),
      createdAt: d.createdAt,
    };
  }

  function draftUpdateMeta(meta) {
    const d = draftLoad() || { items: [], createdAt: Date.now() };
    ['title', 'eventType', 'eventDate', 'dueDate', 'recipientName', 'message'].forEach(function (k) {
      if (meta && meta[k] !== undefined) d[k] = meta[k];
    });
    draftSave(d);
    emit('draft_updated', { draft: getDraft() });
    return getDraft();
  }

  function draftAddItem(productData) {
    if (!productData || !productData.productId) return null;
    const d = draftLoad() || { items: [], createdAt: Date.now() };
    const pid = String(productData.productId);
    const vid = productData.variantId ? String(productData.variantId) : '';
    for (let i = 0; i < d.items.length; i++) {
      if (String(d.items[i].productId) === pid && String(d.items[i].variantId || '') === vid) {
        return { draft: getDraft(), item: d.items[i], existed: true };
      }
    }
    const item = {
      id: 'itm_' + Math.random().toString(36).slice(2, 10),
      productId: pid,
      variantId: vid,
      variantTitle: productData.variantTitle || '',
      handle: productData.handle || '',
      title: productData.title || '',
      imageUrl: productData.imageUrl || '',
      url: productData.url || (productData.handle ? '/products/' + productData.handle : ''),
      price: typeof productData.price === 'number' ? productData.price : parseInt(productData.price || '0', 10),
      quantityWanted: productData.quantityWanted || 1,
      addedAt: Date.now(),
    };
    d.items.push(item);
    if (!draftSave(d)) return null; // localStorage geblokkeerd/vol: geen vals succes tonen
    track('mm_registry_draft_item_added', { product_id: pid, source: productData.source || 'unknown' });
    emit('draft_item_added', { item: item, draft: getDraft() });
    return { draft: getDraft(), item: item, existed: false };
  }

  function draftRemoveItem(itemId) {
    const d = draftLoad();
    if (!d) return null;
    d.items = d.items.filter(function (it) { return it.id !== itemId; });
    draftSave(d);
    track('mm_registry_draft_item_removed', { item_id: itemId });
    emit('draft_item_removed', { itemId: itemId, draft: getDraft() });
    return getDraft();
  }

  // Bevestigings-modal na een gast-add (geen login-muur meer op het verzamelmoment)
  function showDraftConfirm(res) {
    const modal = ensureModal();
    const title = modal.querySelector('[data-mm-rg-modal-title]');
    if (title) title.textContent = 'Tu mesa de regalos';
    const body = modal.querySelector('[data-mm-rg-modal-body]');
    if (body) {
      const count = (res && res.draft && res.draft.items.length) || 0;
      const heading = res && res.existed ? 'Este producto ya está en tu lista' : 'Agregado a tu mesa de regalos ✓';
      body.innerHTML =
        '<div class="mm-rg-modal__login">' +
          '<p style="margin:0 0 0.5rem;font-size:0.9375rem;font-weight:600;">' + heading + '</p>' +
          '<p style="margin:0 0 1rem;font-size:0.875rem;color:rgb(44 62 80 / 0.7);">Tu lista tiene ' + count + ' producto' + (count === 1 ? '' : 's') + '. Se guarda en este navegador; al compartirla la vinculas a tu correo.</p>' +
          '<a href="/pages/mi-lista-de-regalos" data-mm-rg-draft-view style="display:block;width:100%;box-sizing:border-box;padding:0.875rem 1.5rem;background:#2c3e50;color:#fff;border:none;border-radius:999px;font-family:inherit;font-size:0.9375rem;font-weight:600;cursor:pointer;text-align:center;text-decoration:none;">Ver mi lista</a>' +
          '<button type="button" data-mm-rg-draft-continue style="display:block;width:100%;margin-top:0.5rem;padding:0.75rem 1.5rem;background:transparent;color:#2c3e50;border:none;font-family:inherit;font-size:0.875rem;font-weight:600;cursor:pointer;text-decoration:underline;">Seguir comprando</button>' +
        '</div>';
      const cont = body.querySelector('[data-mm-rg-draft-continue]');
      if (cont) cont.addEventListener('click', closePicker);
    }
    modal.classList.add('is-open');
  }

  // ----- Draft-migratie bij login -----
  const MIGRATE_LOCK_KEY = 'mm_registry_draft_migrating';

  function migrateDraft() {
    if (!ENABLED) return Promise.resolve(null);
    const d = draftLoad();
    if (!d || (d.items.length === 0 && !d.title)) return Promise.resolve(null);
    // Cross-tab lock: twee tabs die tegelijk booten zouden anders twee registries maken
    try {
      const lock = parseInt(localStorage.getItem(MIGRATE_LOCK_KEY) || '0', 10);
      if (lock && Date.now() - lock < 30000) return Promise.resolve(null);
      localStorage.setItem(MIGRATE_LOCK_KEY, String(Date.now()));
    } catch (e) { /* swallow */ }
    const releaseLock = function () {
      try { localStorage.removeItem(MIGRATE_LOCK_KEY); } catch (e) { /* swallow */ }
    };
    const itemCount = d.items.length;
    return create({
      title: d.title || 'Mi mesa de regalos',
      eventType: d.eventType || 'baby_shower',
      eventDate: d.eventDate || undefined,
      dueDate: d.dueDate || undefined,
      recipientName: d.recipientName || undefined,
      message: d.message || undefined,
    }).then(function (reg) {
      let chain = Promise.resolve();
      d.items.forEach(function (it) {
        chain = chain.then(function () {
          return addItem(reg.id, it).then(function () {
            // Gemigreerd item direct uit de draft: een deel-failure verliest zo niets
            const cur = draftLoad();
            if (cur) {
              cur.items = cur.items.filter(function (x) { return x.id !== it.id; });
              draftSave(cur);
            }
          }).catch(function (err) {
            console.warn('[mm-registry] draft item migration failed', err);
          });
        });
      });
      // get() is alleen een refresh; bij falen is create's resultaat goed genoeg
      return chain.then(function () {
        return get(reg.id).catch(function () { return reg; });
      });
    }).then(function (reg) {
      const leftover = draftLoad();
      if (!leftover || leftover.items.length === 0) {
        draftClear();
      } else {
        showToast('Algunos productos no se pudieron guardar; inténtalo de nuevo.');
      }
      releaseLock();
      track('mm_registry_draft_migrated', { registry_id: reg.id, items: itemCount });
      emit('draft_migrated', { registry: reg });
      showToast('Tu lista está guardada en tu cuenta ✓');
      return reg;
    }).catch(function (err) {
      // Draft blijft staan; volgende pageload probeert het opnieuw.
      releaseLock();
      console.warn('[mm-registry] draft migration failed', err);
      return null;
    });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function renderPickerList(productData) {
    const modal = ensureModal();
    const body = modal.querySelector('[data-mm-rg-modal-body]');
    const registries = state.registries.slice();

    const createBtn =
      '<button type="button" class="mm-rg-modal__item" data-mm-rg-create-new>' +
        '<span class="mm-rg-modal__item-icon">' +
          '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M8 3v10M3 8h10"/></svg>' +
        '</span>' +
        '<span class="mm-rg-modal__item-text">' +
          '<span class="mm-rg-modal__item-title">Crear nueva lista</span>' +
          '<span class="mm-rg-modal__item-meta">Empieza una lista nueva con este producto</span>' +
        '</span>' +
      '</button>';

    let html = createBtn;
    if (registries.length > 0) {
      html += '<hr class="mm-rg-modal__divider">';
      html += registries.map(function (r) {
        const count = (r.items || []).length;
        // Find matching item by productId + variantId (so different variants are treated independently)
        const matchingItem = (r.items || []).find(function (it) {
          return String(it.productId) === String(productData.productId) &&
                 String(it.variantId || '') === String(productData.variantId || '');
        });
        const qtyInList = matchingItem ? matchingItem.quantityWanted : 0;
        return (
          '<button type="button" class="mm-rg-modal__item" data-mm-rg-pick="' + escapeHtml(r.id) + '">' +
            '<span class="mm-rg-modal__item-icon">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/></svg>' +
            '</span>' +
            '<span class="mm-rg-modal__item-text">' +
              '<span class="mm-rg-modal__item-title">' + escapeHtml(r.title) + '</span>' +
              '<span class="mm-rg-modal__item-meta">' + count + ' producto' + (count === 1 ? '' : 's') + (qtyInList > 0 ? ' · ' + qtyInList + ' en la lista (click = +1)' : '') + '</span>' +
            '</span>' +
          '</button>'
        );
      }).join('');
    }
    body.innerHTML = html;

    body.querySelectorAll('[data-mm-rg-pick]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const rid = btn.getAttribute('data-mm-rg-pick');
        btn.setAttribute('disabled', '');
        addItem(rid, productData).then(function (reg) {
          showToast('Agregado a "' + reg.title + '"');
          closePicker();
        }).catch(function (err) {
          showToast('No pudimos agregar. Intenta de nuevo.');
          console.warn('[mm-registry] pick failed', err);
          btn.removeAttribute('disabled');
        });
      });
    });

    const createNew = body.querySelector('[data-mm-rg-create-new]');
    if (createNew) {
      createNew.addEventListener('click', function () {
        const title = prompt('Nombra tu mesa de regalos:', 'Mi mesa de regalos');
        if (!title) return;
        createNew.setAttribute('disabled', '');
        create({ title: title.trim(), eventType: 'other' }).then(function (reg) {
          return addItem(reg.id, productData).then(function () { return reg; });
        }).then(function (reg) {
          showToast('Lista creada y producto agregado');
          closePicker();
          // Optional: small delay then redirect to the new registry
          setTimeout(function () {
            if (confirm('¿Quieres ir a tu lista ahora para personalizarla?')) {
              window.location.href = '/pages/mi-lista-de-regalos?id=' + encodeURIComponent(reg.id);
            }
          }, 300);
        }).catch(function (err) {
          showToast('No pudimos crear la lista.');
          console.warn('[mm-registry] create failed', err);
          createNew.removeAttribute('disabled');
        });
      });
    }
  }

  function openPicker(productData) {
    if (!ENABLED) {
      // Gast: voeg direct toe aan de lokale draft-lijst. Geen login-muur meer
      // op het verzamelmoment; het account komt pas bij het delen (owner-pagina).
      if (productData && productData.productId) {
        const res = draftAddItem(productData);
        if (res) { showDraftConfirm(res); } else { showLoginExplainer(); }
      } else {
        showLoginExplainer();
      }
      return;
    }
    const modal = ensureModal();
    modal.classList.add('is-open');

    // Show loading then list
    const body = modal.querySelector('[data-mm-rg-modal-body]');
    if (state.registries.length > 0 || state.ready) {
      renderPickerList(productData);
    } else {
      body.innerHTML = '<p class="mm-rg-modal__loading">Cargando tus listas...</p>';
      list().then(function () { renderPickerList(productData); });
    }
  }

  // ----- Boot -----

  // Migratie-promise zodat pagina's (owner) erop kunnen wachten voordat ze list() tonen
  let migrationPromise = null;

  function boot() {
    state.ready = true;
    emit('ready', { registries: state.registries.slice() });
    if (ENABLED) {
      // Eerst een eventuele gast-draft naar de server migreren, dan de lijst laden
      migrationPromise = migrateDraft();
      migrationPromise.then(function () { list(); });
    }
  }

  window.MMRegistry = {
    list: list,
    create: create,
    get: get,
    update: update,
    delete: del,
    addItem: addItem,
    removeItem: removeItem,
    updateItem: updateItem,
    isItemInAnyRegistry: isItemInAnyRegistry,
    shareUrl: shareUrl,
    openPicker: openPicker,
    closePicker: closePicker,
    on: on,
    showToast: showToast,
    isEnabled: function () { return ENABLED; },
    isReady: function () { return state.ready; },
    getCached: function () { return state.registries.slice(); },
    // Gast-draft API
    hasDraft: hasDraft,
    getDraft: getDraft,
    draftAddItem: draftAddItem,
    draftRemoveItem: draftRemoveItem,
    draftUpdateMeta: draftUpdateMeta,
    draftClear: draftClear,
    showLoginExplainer: showLoginExplainer,
    migration: function () { return migrationPromise || Promise.resolve(null); },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
